import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '@rematter/common/dateTime';
import {
  CurrencyCode,
  DeductionApplicationType,
  enumUnit,
  FreightTerms,
  PurchaseLoadStatus,
  SalesItemLineType,
  SettlementBasis,
  Unit,
  WeightInput,
  WorkOrderStatus,
} from '@rematter/frontend-lib-api-client';

import {
  PurchaseLoadLineItemRecord,
  PurchaseLoadRecord,
} from '../types/purchaseLoad';
import { toMoney, zeroMoney } from '../utils/money';

import { purchaseLoadLineItemFragment } from './fragmentsPurchaseLoad';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

type RawLineItem = {
  id: string;
  parentLineItemId?: string | null;
  type: string;
  itemId?: string | null;
  tagId?: string | null;
  purchaseOrderLineId?: string | null;
  grossWeight: WeightInput | null;
  tareWeight: WeightInput | null;
  netWeight: WeightInput | null;
  unitPrice?: { amount: number; currencyCode: CurrencyCode } | null;
  estimatedPrice?: { amount: number; currencyCode: CurrencyCode } | null;
  pricingUnit?: Unit | null;
  requiresSorting: boolean;
  settlementBasis: string;
  sortingWorkOrder?: {
    id: string;
    status: string;
    workOrderNumber: string;
  } | null;
  notes?: string | null;
  vehicleVin?: string | null;
  vehicleYear?: number | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleTitleStatus?: string | null;
  catSerialNumber?: string | null;
  catOemCode?: string | null;
  catComplianceStatus?: string | null;
  item?: { id: string; name: string } | null;
};

const mapLineItem = (lineItem: RawLineItem): PurchaseLoadLineItemRecord => ({
  id: lineItem.id,
  parentLineItemId: lineItem.parentLineItemId ?? null,
  type: lineItem.type as SalesItemLineType,
  itemId: lineItem.itemId || null,
  tagId: lineItem.tagId || null,
  purchaseOrderLineId: lineItem.purchaseOrderLineId || null,
  grossWeight: lineItem.grossWeight
    ? { ...lineItem.grossWeight, __typename: 'Weight' as const }
    : null,
  tareWeight: lineItem.tareWeight
    ? { ...lineItem.tareWeight, __typename: 'Weight' as const }
    : null,
  netWeight: lineItem.netWeight
    ? { ...lineItem.netWeight, __typename: 'Weight' as const }
    : null,
  unitPrice: lineItem.unitPrice ? toMoney(lineItem.unitPrice) : null,
  estimatedPrice: lineItem.estimatedPrice
    ? toMoney(lineItem.estimatedPrice)
    : null,
  pricingUnit: lineItem.pricingUnit ?? enumUnit.Pounds,
  requiresSorting: lineItem.requiresSorting,
  settlementBasis: lineItem.settlementBasis as SettlementBasis,
  sortingWorkOrder: lineItem.sortingWorkOrder
    ? {
        id: lineItem.sortingWorkOrder.id,
        status: lineItem.sortingWorkOrder.status as WorkOrderStatus,
        workOrderNumber: lineItem.sortingWorkOrder.workOrderNumber,
      }
    : null,
  notes: lineItem.notes || null,
  vehicleVin: lineItem.vehicleVin || null,
  vehicleYear: lineItem.vehicleYear ?? null,
  vehicleMake: lineItem.vehicleMake || null,
  vehicleModel: lineItem.vehicleModel || null,
  vehicleTitleStatus: lineItem.vehicleTitleStatus || null,
  catSerialNumber: lineItem.catSerialNumber || null,
  catOemCode: lineItem.catOemCode || null,
  catComplianceStatus: lineItem.catComplianceStatus || null,
  item: lineItem.item || null,
});

export const purchaseLoadDetailQueryAtom = createQueryAtomFactory(
  'trade.purchaseLoadDetail',
  (params: { id: string }) => ({
    purchaseLoad: {
      __args: {
        id: params.id,
      },
      id: true,
      version: true,
      status: true,
      documentNumber: true,
      currencyCode: true,
      receivedDate: true,
      description: true,
      heavyWeight: {
        value: true,
        unit: true,
      },
      lightWeight: {
        value: true,
        unit: true,
      },
      weightDisplayUnit: true,
      purchaseOrderId: true,
      purchaseOrder: {
        id: true,
        documentNumber: true,
      },
      facilityId: true,
      facility: {
        id: true,
        facilityName: true,
      },
      supplierAccountId: true,
      supplierAccount: {
        id: true,
        name: true,
      },
      supplierAddressId: true,
      supplierAddress: {
        id: true,
        name: true,
        address: {
          __scalar: true,
        },
      },
      containerTypeId: true,
      containerType: {
        id: true,
        name: true,
      },
      carrierAccountId: true,
      carrierAccount: {
        id: true,
        name: true,
      },
      sourceLoadId: true,
      sourceFacilityId: true,
      sourceFacility: {
        id: true,
        facilityName: true,
      },
      serviceId: true,
      service: {
        id: true,
        documentNumber: true,
      },
      freightCost: {
        amount: true,
        currencyCode: true,
      },
      freightTerms: true,
      bolNumber: true,
      shippingCarrierName: true,
      scac: true,
      driverName: true,
      truckNumber: true,
      trailerNumber: true,
      containerNumber: true,
      sealNumber: true,
      vehicleLicensePlate: true,
      internalNotes: true,
      externalNotes: true,
      receivingTicketNumber: true,
      receivedTicketIssueDate: true,
      lineItems: {
        ...purchaseLoadLineItemFragment,
      },
      deductions: {
        id: true,
        deductionTypeId: true,
        applicationType: true,
        deductionType: { id: true, name: true, applicationType: true },
        weightValue: { value: true, unit: true },
        priceValue: { amount: true, currencyCode: true },
        percentage: true,
        notes: true,
        purchaseLoadLineItemId: true,
        calculatedAmount: { amount: true, currencyCode: true },
      },
      payments: {
        id: true,
        purchaseLoadId: true,
        paymentMethod: true,
        paymentTerm: true,
        amount: {
          amount: true,
          currencyCode: true,
        },
        percentage: true,
        legalHold: true,
        financialAccountId: true,
        checkNumber: true,
        checkMemo: true,
        roundingMethod: true,
        roundingPrecision: true,
      },
      files: {
        id: true,
        fileName: true,
        fileSize: true,
        mimeType: true,
        fileType: true,
        metadata: true,
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
      createdByUser: {
        id: true,
        firstName: true,
        lastName: true,
      },
    },
  }),
  {
    select: (data): PurchaseLoadRecord | null => {
      const purchaseLoad = data.purchaseLoad;
      if (!purchaseLoad) {
        return null;
      }

      const receivedDate = parseZonedDateTime(purchaseLoad.receivedDate);
      if (!receivedDate) {
        throw new Error('Purchase load missing required receivedDate');
      }

      return {
        id: purchaseLoad.id,
        status: purchaseLoad.status as PurchaseLoadStatus,
        documentNumber: purchaseLoad.documentNumber,
        currencyCode: purchaseLoad.currencyCode as CurrencyCode,
        receivedDate,
        description: purchaseLoad.description || null,
        heavyWeight: purchaseLoad.heavyWeight
          ? { ...purchaseLoad.heavyWeight, __typename: 'Weight' as const }
          : null,
        lightWeight: purchaseLoad.lightWeight
          ? { ...purchaseLoad.lightWeight, __typename: 'Weight' as const }
          : null,
        weightDisplayUnit: purchaseLoad.weightDisplayUnit,
        purchaseOrderId: purchaseLoad.purchaseOrderId || null,
        purchaseOrder: purchaseLoad.purchaseOrder || null,
        facilityId: purchaseLoad.facilityId,
        facility: purchaseLoad.facility,
        supplierAccountId: purchaseLoad.supplierAccountId || null,
        supplierAccount: purchaseLoad.supplierAccount || null,
        supplierAddressId: purchaseLoad.supplierAddressId || null,
        supplierAddress: purchaseLoad.supplierAddress || null,
        containerTypeId: purchaseLoad.containerTypeId || null,
        containerType: purchaseLoad.containerType || null,
        carrierAccountId: purchaseLoad.carrierAccountId || null,
        carrierAccount: purchaseLoad.carrierAccount || null,
        inboundTollingAgreementId: null,
        sourceLoadId: purchaseLoad.sourceLoadId || null,
        sourceFacilityId: purchaseLoad.sourceFacilityId || null,
        sourceFacility: purchaseLoad.sourceFacility || null,
        serviceId: purchaseLoad.serviceId ?? null,
        service: purchaseLoad.service ?? null,
        freightCost: purchaseLoad.freightCost
          ? toMoney(purchaseLoad.freightCost)
          : null,
        freightTerms: (purchaseLoad.freightTerms as FreightTerms) || null,
        bolNumber: purchaseLoad.bolNumber || null,
        shippingCarrierName: purchaseLoad.shippingCarrierName || null,
        scac: purchaseLoad.scac || null,
        driverName: purchaseLoad.driverName || null,
        truckNumber: purchaseLoad.truckNumber || null,
        trailerNumber: purchaseLoad.trailerNumber || null,
        containerNumber: purchaseLoad.containerNumber || null,
        sealNumber: purchaseLoad.sealNumber || null,
        vehicleLicensePlate: purchaseLoad.vehicleLicensePlate || null,
        internalNotes: purchaseLoad.internalNotes || null,
        externalNotes: purchaseLoad.externalNotes || null,
        receivingTicketNumber: purchaseLoad.receivingTicketNumber || null,
        receivedTicketIssueDate:
          maybeParseCalendarDate(purchaseLoad.receivedTicketIssueDate) ?? null,
        grandTotal: zeroMoney(),
        lineItems: purchaseLoad.lineItems?.map(mapLineItem),
        deductions: purchaseLoad.deductions?.map((deduction) => ({
          id: deduction.id,
          deductionTypeId: deduction.deductionTypeId,
          deductionTypeName: deduction.deductionType?.name ?? '',
          applicationType:
            deduction.applicationType as DeductionApplicationType,
          weightValue: deduction.weightValue
            ? { ...deduction.weightValue, __typename: 'Weight' as const }
            : null,
          priceValue: deduction.priceValue
            ? toMoney(deduction.priceValue)
            : null,
          percentage: deduction.percentage ?? null,
          notes: deduction.notes || null,
          purchaseLoadLineItemId: deduction.purchaseLoadLineItemId || null,
          calculatedAmount: deduction.calculatedAmount
            ? toMoney(deduction.calculatedAmount)
            : null,
        })),
        payments: purchaseLoad.payments?.map((payment) => ({
          id: payment.id,
          purchaseLoadId: payment.purchaseLoadId,
          paymentMethod: payment.paymentMethod,
          paymentTerm: payment.paymentTerm,
          amount: {
            amount: payment.amount.amount,
            currencyCode: payment.amount.currencyCode as CurrencyCode,
          },
          percentage: payment.percentage ?? null,
          legalHold: payment.legalHold,
          financialAccountId: payment.financialAccountId || null,
          checkNumber: payment.checkNumber || null,
          checkMemo: payment.checkMemo || null,
          roundingMethod: payment.roundingMethod || null,
          roundingPrecision: payment.roundingPrecision || null,
        })),
        files: purchaseLoad.files?.map((file) => ({
          id: file.id,
          fileName: file.fileName,
          fileSize: file.fileSize ?? undefined,
          mimeType: file.mimeType,
          fileType: file.fileType ?? undefined,
          metadata: file.metadata as Record<string, unknown> | undefined,
        })),
        timestamps: {
          createdAt: parseZonedDateTime(purchaseLoad.timestamps.createdAt),
          updatedAt: parseZonedDateTime(purchaseLoad.timestamps.updatedAt),
        },
        createdByUser: purchaseLoad.createdByUser || null,
      };
    },
  },
);
